import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal, Pin } from 'lucide-react';

const CLOSE_DELAY_MS = 180;
// The click that follows the hover that opened the menu keeps it open: while the pointer
// is still on the dots, or within this long if it slipped off and back.
const HOVER_CLICK_MS = 1500;

/**
 * Admin configuration, as a flat list that opens beside the three dots.
 * Each page can be pinned onto the main sidebar.
 */
export default function SidebarMoreMenu({
  items,
  pinnedPages,
  activePage,
  showLabels = false,
  inline = false,
  onTogglePin,
  onSelect,
  onOpenChange,
}) {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState({ left: 16, top: 16, maxHeight: 480 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const closeTimer = useRef(null);
  // When pointing at the dots opened the menu, so the click that usually follows doesn't close it.
  const hoverOpenedAt = useRef(null);
  const pointerStayed = useRef(false);
  const pinned = new Set(pinnedPages);
  const triggerCurrent = items.some((item) => item.page === activePage) && !pinned.has(activePage);

  const cancelClose = () => clearTimeout(closeTimer.current);
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  useEffect(() => {
    if (!open) hoverOpenedAt.current = null;
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (!open || inline) return undefined;
    const place = () => {
      const bounds = triggerRef.current?.getBoundingClientRect();
      if (!bounds) return;
      const top = Math.max(8, Math.min(bounds.top, window.innerHeight - 280));
      const next = {
        left: bounds.right + 8,
        top,
        maxHeight: Math.max(160, window.innerHeight - top - 8),
      };
      setPlacement((current) => (
        current.left === next.left && current.top === next.top && current.maxHeight === next.maxHeight
          ? current
          : next
      ));
    };
    place();
    // The rail may still be opening when the menu does; follow the trigger as it widens.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    if (triggerRef.current) observer?.observe(triggerRef.current);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    const dismiss = (event) => {
      if (!menuRef.current?.contains(event.target) && !triggerRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('keydown', escape);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('keydown', escape);
    };
  }, [open, inline, items.length]);

  const choose = (item) => {
    setOpen(false);
    onSelect?.(item.page);
  };

  const trigger = (
    <button
      type="button"
      ref={triggerRef}
      aria-label="More"
      aria-haspopup="menu"
      aria-expanded={open}
      aria-current={triggerCurrent ? 'page' : undefined}
      onMouseEnter={() => {
        if (inline) return;
        cancelClose();
        if (!open) {
          hoverOpenedAt.current = Date.now();
          pointerStayed.current = true;
        }
        setOpen(true);
      }}
      onMouseLeave={() => {
        pointerStayed.current = false;
        if (!inline) scheduleClose();
      }}
      onClick={() => {
        cancelClose();
        const followsHover = hoverOpenedAt.current !== null
          && (pointerStayed.current || Date.now() - hoverOpenedAt.current < HOVER_CLICK_MS);
        hoverOpenedAt.current = null;
        if (followsHover) {
          setOpen(true);
          return;
        }
        setOpen((value) => !value);
      }}
      className={`relative flex w-full min-w-0 items-center rounded-lg text-slate-600 transition-[background-color,color,padding] duration-200 ease-out ${
        showLabels ? 'justify-start px-2.5 py-2' : 'justify-start px-2.5 py-2.5'
      } ${triggerCurrent || open ? 'bg-slate-900/[0.06] text-slate-900' : 'hover:bg-slate-900/[0.04]'}`}
    >
      <MoreHorizontal className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} aria-hidden />
    </button>
  );

  const rows = (
    <div role="menu" aria-label="More" className={inline ? 'py-1' : ''}>
      {items.map((item) => {
        const isPinned = pinned.has(item.page);
        const isActive = item.page === activePage;
        const pinLabel = isPinned ? `Unpin ${item.label} from the sidebar` : `Pin ${item.label} to the sidebar`;
        return (
          <div
            key={item.page}
            className={`group/row flex items-center rounded-lg pr-1 ${isActive ? 'bg-slate-100' : 'hover:bg-slate-100'}`}
          >
            <button
              type="button"
              role="menuitem"
              aria-current={isActive ? 'page' : undefined}
              onClick={() => choose(item)}
              className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium text-slate-700"
            >
              <item.Icon className="h-4 w-4 shrink-0 text-slate-500" strokeWidth={1.75} aria-hidden />
              <span className="truncate">{item.label}</span>
              {item.hasChanges ? (
                <span aria-hidden="true" data-testid="branch-change-mark" className="ml-auto h-2 w-2 shrink-0 rounded-full bg-indigo-500" />
              ) : null}
            </button>
            <button
              type="button"
              aria-label={pinLabel}
              title={pinLabel}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onTogglePin?.(item.page);
              }}
              className={`rounded-md p-1.5 text-slate-400 hover:bg-white hover:text-slate-700 ${
                isPinned ? 'text-slate-600' : 'opacity-0 group-hover/row:opacity-100 focus:opacity-100'
              }`}
            >
              <Pin className={`h-3.5 w-3.5 ${isPinned ? 'fill-slate-600' : ''}`} aria-hidden />
            </button>
          </div>
        );
      })}
    </div>
  );

  if (inline) {
    return (
      <div>
        {trigger}
        {open ? rows : null}
      </div>
    );
  }

  return (
    <div>
      {trigger}
      {open
        ? createPortal(
            <div
              ref={menuRef}
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              style={placement}
              className="fixed z-[60] w-56 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 text-sm shadow-xl ring-1 ring-black/5"
            >
              {rows}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
